import React, { useEffect, useState } from 'react'
import { useAuth } from '@/core/auth/AuthContext'
import { VoipPanel } from './VoipPanel'
import { CallAlertOverlay, NotificationPermissionChip } from './CallAlertOverlay'
import { noteVoipPhase, type VoipPhaseDetail } from './incoming-call-bus'
import './voip-launcher.css'

// VoipLauncher — non-invasive entry point for the embeddable softphone.
//
// • Renders a floating phone button in the bottom-right corner whose
//   color/animation reflects the widget's live phase (available, away,
//   offline, incoming, active) via the `pkv:phase` broadcast.
// • Mounts the fullscreen CallAlertOverlay so an incoming call is
//   impossible to miss even with the panel closed (the ringtone itself
//   lives inside @pickit/voip-widget).
// • Only mounted for users whose role is 'admin' or 'bureau' (others
//   get a 403 from the VoIP API anyway).
// • Clicking the button toggles the widget panel visibility. The
//   panel itself runs continuously once opened — closing just hides
//   the UI; SIP registration and incoming-call notifications keep
//   working in the background.
//
// Drop this inside any layout that admins/bureau users see. Putting
// it in `MainLayout` (or each role's layout) makes it globally
// available with no further wiring.

// 2026-09-17 — VOIP COUPÉE CÔTÉ V1 (bascule vers la V2). Le PBX n'accepte qu'un téléphone par agent
// (max_contacts = 1, remove_existing) : V1 et V2 ouverts en même temps s'éjectaient mutuellement toutes
// les 30 s. Le softphone vit désormais dans la V2. Pour réactiver ici : VITE_VOIP_ENABLED=true.
const V1_VOIP_ENABLED = import.meta.env.VITE_VOIP_ENABLED === 'true'

export const VoipLauncher: React.FC = () => {
  const { isAdmin, isBureau } = useAuth()
  const [open, setOpen] = useState(false)
  const [phase, setPhase] = useState<VoipPhaseDetail['phase']>('idle')
  const [status, setStatus] = useState<VoipPhaseDetail['status']>('available')
  const [registered, setRegistered] = useState(true)

  // The widget dispatches `pkv:focus-widget` when the agent clicks an
  // OS notification or the overlay's "Voir dans le téléphone". We
  // auto-open the panel so they can act without an extra click.
  useEffect(() => {
    const onFocus = () => setOpen(true)
    window.addEventListener('pkv:focus-widget', onFocus)
    return () => window.removeEventListener('pkv:focus-widget', onFocus)
  }, [])

  // Live phase broadcast from the widget → launcher visual state +
  // incoming-call bus (fullscreen overlay, title flash, notification).
  useEffect(() => {
    const onPhase = (e: Event) => {
      const detail = (e as CustomEvent<VoipPhaseDetail>).detail
      if (!detail) return
      setPhase(detail.phase)
      setStatus(detail.status)
      setRegistered(detail.registered)
      noteVoipPhase(detail)
      if (detail.phase === 'incoming') setOpen(true)
    }
    window.addEventListener('pkv:phase', onPhase)
    return () => window.removeEventListener('pkv:phase', onPhase)
  }, [])

  if (!V1_VOIP_ENABLED) return null // coupure 2026-09-17 — voir en tête de fichier
  if (!isAdmin && !isBureau) return null

  const stateClass =
    phase === 'incoming'
      ? 'voip-launcher--incoming'
      : phase === 'active' || phase === 'dialing'
        ? 'voip-launcher--active'
        : !registered
          ? 'voip-launcher--offline'
          : status === 'away'
            ? 'voip-launcher--away'
            : 'voip-launcher--available'

  return (
    <>
      <button
        type="button"
        aria-label="Téléphonie"
        onClick={() => setOpen((v) => !v)}
        className={`voip-launcher ${stateClass}${open ? ' voip-launcher--open' : ''}`}
      >
        📞
      </button>

      {!open && <NotificationPermissionChip />}
      <CallAlertOverlay />

      {/* Always-mounted so JsSIP connects + REGISTERs at login time
          rather than waiting for the agent to click the button.
          Without this, the widget only registers when first opened
          and inbound calls in the meantime hit "no one online" and
          drop to voicemail. We just toggle visibility on click. */}
      <div
        style={{
          position: 'fixed',
          right: 20,
          bottom: 88,
          display: open ? 'block' : 'none',
          zIndex: 998,
        }}
      >
        <VoipPanel />
      </div>
    </>
  )
}

export default VoipLauncher
